import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AttestedDocument } from "../../models/attestedDocument";
import { formatDocumentDate, formatDocumentDateTime } from "../../utils";

const Cell = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <Box
    sx={theme => ({
      backgroundColor: theme.palette.background.paper,
      p: "13px 16px",
    })}
  >
    <Typography
      component="dt"
      sx={theme => ({
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        fontWeight: 700,
        mb: "3px",
        color: theme.palette.text.secondary,
      })}
    >
      {label}
    </Typography>
    <Typography
      component="dd"
      sx={theme => ({
        m: 0,
        fontSize: mono ? "12px" : "14px",
        fontWeight: mono ? 500 : 600,
        fontFamily: mono ? "monospace" : undefined,
        wordBreak: mono ? "break-all" : undefined,
        color: theme.palette.text.primary,
      })}
    >
      {value}
    </Typography>
  </Box>
);

// The lapsed case keeps the reference and both dates and drops the rest: it
// still has to prove the reference was real, without implying a live copy.
const VerifyMetadata = ({
  attested,
  trimmed = false,
}: {
  attested: AttestedDocument;
  trimmed?: boolean;
}) => {
  const { t } = useTranslation();

  const reportName = t(`verify.kind.${attested.kind}`, {
    defaultValue: attested.kind,
  });

  return (
    <Box
      component="dl"
      sx={theme => ({
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        gap: "1px",
        my: 3,
        backgroundColor: theme.palette.divider,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        overflow: "hidden",
      })}
    >
      <Cell
        label={t("verify.metadata.reference")}
        value={attested.reference}
        mono
      />
      <Cell
        label={t("verify.metadata.issued")}
        value={formatDocumentDateTime(attested.generated_at)}
      />
      <Cell
        label={t("verify.metadata.retained_until")}
        value={formatDocumentDate(attested.expires_at)}
      />
      {!trimmed && (
        <>
          <Cell label={t("verify.metadata.report")} value={reportName} />
          <Cell
            label={t("verify.metadata.issued_to")}
            value={attested.issued_to}
          />
          <Cell
            label={t("verify.metadata.content_hash")}
            value={attested.content_hash}
            mono
          />
        </>
      )}
    </Box>
  );
};

export default VerifyMetadata;
